import * as React from "react";
import { Settings, Tag, Users, Mail, Check, LogOut, X, MapPin, Sparkles, Bell, Shield, FileText } from "lucide-react";
import { useLocation } from "wouter";
import { useAuth } from "@/contexts/AuthContext.js";
import { useDashboard } from "@/contexts/DashboardContext.js";
import { useFeatureFlags } from "@/contexts/FeatureFlagContext.js";
import { usePlan } from "@/contexts/PlanContext.js";
import { cn } from "@/lib/utils.js";
import { apiFetch } from "@/lib/api.js";
import { Modal } from "@/components/Modal.js";
import { BulkSpacesWizard } from "@/components/BulkSpacesWizard.js";
import { useToast } from "@/components/Toast.js";
import { AVAILABLE_FIELDS } from "@homethyst/shared/schemas";

interface Invitation { id: string; email: string; status: string; invitedByName: string; householdId: string; }
interface Space { id: string; name: string; type: string; parentId: string | null; }

export default function SettingsPage() {
  const { user, logout, refresh } = useAuth();
  const [, navigate] = useLocation();
  const { refreshFlags } = useFeatureFlags();
  const { usage } = usePlan();
  const { refresh: refreshDashboard } = useDashboard();
  const toast = useToast();
  const [inviteEmail, setInviteEmail] = React.useState("");
  const [invitations, setInvitations] = React.useState<{ received: Invitation[]; sent: Invitation[] }>({ received: [], sent: [] });
  const [quickAddFields, setQuickAddFields] = React.useState<string[]>([]);
  const [aiEnabled, setAiEnabled] = React.useState(true);

  // Spaces bulk add
  const [spaces, setSpaces] = React.useState<Space[]>([]);
  const [showBulk, setShowBulk] = React.useState(false);

  const fetchSpaces = React.useCallback(() => apiFetch<Space[]>("/api/spaces").then(setSpaces).catch(() => {}), []);

  const fetchInvitations = () =>
    apiFetch<{ received?: Invitation[]; sent?: Invitation[] }>("/api/invitations")
      // Defend against a malformed/unexpected payload (e.g. an array or partial
      // object): always normalize to the { received, sent } shape the UI reads.
      .then((data) => setInvitations({ received: data?.received ?? [], sent: data?.sent ?? [] }))
      .catch(() => {});

  React.useEffect(() => {
    fetchInvitations();
    fetchSpaces();
    apiFetch<{ quickAddFields?: string[]; aiEnabled?: boolean }>("/api/settings").then((s) => { setQuickAddFields(s?.quickAddFields ?? []); setAiEnabled(s?.aiEnabled ?? true); }).catch(() => {});
  }, []);

  const sendInvite = async () => {
    if (!inviteEmail.trim()) return;
    try {
      await apiFetch("/api/invitations", { method: "POST", body: JSON.stringify({ email: inviteEmail }) });
      setInviteEmail("");
      toast.show("Invitation sent!");
      fetchInvitations();
    } catch (e: any) { toast.show(e.message ?? "Failed to send invitation", "error"); }
  };

  const acceptInvite = async (id: string) => {
    try {
      await apiFetch(`/api/invitations/${id}/accept`, { method: "PUT" });
      toast.show("Invitation accepted!");
      fetchInvitations(); refresh();
    } catch (e: any) { toast.show(e.message ?? "Failed to accept", "error"); }
  };

  const rejectInvite = async (id: string) => {
    try {
      await apiFetch(`/api/invitations/${id}/reject`, { method: "PUT" });
      toast.show("Invitation declined");
      fetchInvitations();
    } catch (e: any) { toast.show(e.message ?? "Failed to reject", "error"); }
  };

  const ALL_FIELDS = AVAILABLE_FIELDS;
  const toggleField = async (field: string) => {
    const next = quickAddFields.includes(field) ? quickAddFields.filter((f) => f !== field) : [...quickAddFields, field];
    setQuickAddFields(next);
    try {
      await apiFetch("/api/settings", { method: "PUT", body: JSON.stringify({ quickAddFields: next }) });
      refreshDashboard();
    } catch { toast.show("Failed to save setting", "error"); setQuickAddFields(quickAddFields); }
  };

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-semibold text-amethyst-ink flex items-center gap-2 font-headline tracking-[-0.01em]"><Settings className="w-6 h-6 text-primary" />Settings</h1>

      {/* Notifications */}
      <a href="/settings/notifications" className="flex items-center gap-3 bg-card rounded-lg border border-clay-border p-5 hover:border-primary/40 transition-colors">
        <Bell className="w-5 h-5 text-primary" /><div><div className="text-sm font-semibold text-ink">Notifications</div><div className="text-[0.8125rem] text-faint">Low stock alerts and reminders</div></div>
      </a>

      {/* Profile */}
      <section className="bg-card rounded-lg border border-clay-border p-6">
        <h2 className="font-semibold text-ink mb-4">Profile</h2>
        <div className="text-sm space-y-1 text-body">
          <p><span className="text-faint">Email:</span> {user?.email}</p>
          <p><span className="text-faint">Household:</span> {user?.householdName}</p>
        </div>

        {usage && (
          <div className="mt-4">
            <div className="flex items-center gap-2 mb-4">
              <span className={cn(
                "px-3 py-1 rounded-pill text-[0.8125rem] font-semibold",
                usage.plan === "pro" ? "bg-sage-wash text-sage-deep" : usage.plan === "plus" ? "bg-amethyst-soft text-amethyst-ink" : "bg-clay-muted text-faint"
              )}>
                {usage.label}
              </span>
              {usage.adsEnabled && <span className="text-[0.8125rem] text-faint">Ad-supported</span>}
            </div>

            <div className="space-y-3 text-sm">
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-body">AI actions</span>
                  <span className="text-faint tabular-nums">
                    {usage.aiActionsLimit === -1 ? "Unlimited" : `${usage.aiActionsUsed} / ${usage.aiActionsLimit}`}
                  </span>
                </div>
                {usage.aiActionsLimit !== -1 && (
                  <div className="h-2 rounded-pill bg-clay-muted overflow-hidden">
                    <div
                      className={cn("h-full rounded-pill transition-all", usage.aiActionsUsed / usage.aiActionsLimit >= 0.8 ? "bg-warning" : "bg-primary")}
                      style={{ width: `${Math.min(100, (usage.aiActionsUsed / usage.aiActionsLimit) * 100)}%` }}
                    />
                  </div>
                )}
              </div>
              <div>
                <div className="flex justify-between mb-1">
                  <span className="text-body">Homes</span>
                  <span className="text-faint tabular-nums">
                    {usage.homesLimit === -1 ? `${usage.homesUsed} (unlimited)` : `${usage.homesUsed} / ${usage.homesLimit}`}
                  </span>
                </div>
                {usage.homesLimit !== -1 && (
                  <div className="h-2 rounded-pill bg-clay-muted overflow-hidden">
                    <div className="h-full rounded-pill bg-primary transition-all" style={{ width: `${Math.min(100, (usage.homesUsed / usage.homesLimit) * 100)}%` }} />
                  </div>
                )}
              </div>
              <div className="flex justify-between">
                <span className="text-body">Photos</span>
                <span className={usage.photosEnabled ? "text-success-text text-[0.8125rem]" : "text-faint text-[0.8125rem]"}>{usage.photosEnabled ? "Enabled" : "Pro only"}</span>
              </div>
            </div>

            {usage.plan !== "pro" && (
              <div className="mt-5 rounded-lg bg-amethyst-soft border border-clay-border p-4">
                <p className="text-sm font-medium text-ink mb-1">
                  {usage.plan === "free" ? "Unlock more with Plus or Pro" : "Go Pro for the full experience"}
                </p>
                <ul className="text-[0.8125rem] text-body space-y-1 mb-3">
                  {usage.plan === "free" && (
                    <>
                      <li>✦ 100 AI actions/month with Plus ($5/mo)</li>
                      <li>✦ Unlimited AI, homes & photos with Pro ($10/mo)</li>
                      <li>✦ No ads on any paid plan</li>
                    </>
                  )}
                  {usage.plan === "plus" && (
                    <>
                      <li>✦ Unlimited AI actions & homes</li>
                      <li>✦ Photo attachments on items</li>
                    </>
                  )}
                </ul>
                <button onClick={() => navigate("/pricing")} className="px-4 py-2 rounded-md bg-primary text-white text-sm font-medium hover:bg-amethyst-ink transition-colors">
                  View Plans
                </button>
              </div>
            )}
          </div>
        )}
        <button onClick={logout} className="mt-4 flex items-center gap-2 text-sm text-faint hover:text-body transition-colors"><LogOut className="w-4 h-4" />Sign out</button>
      </section>

      {/* Add Item Fields */}
      <section className="bg-card rounded-lg border border-clay-border p-6">
        <h2 className="font-semibold text-ink mb-4">Add Item Fields</h2>
        <p className="text-sm text-body mb-4">Choose which fields appear in the Add Item form. All other fields are available when editing an item.</p>
        <div className="space-y-2">
          {ALL_FIELDS.map(({ key, label }) => (
            <button key={key} onClick={() => toggleField(key)} aria-pressed={quickAddFields.includes(key)} className={cn("w-full flex items-center gap-3 p-3 rounded-md border text-left text-sm transition-colors", quickAddFields.includes(key) ? "bg-amethyst-soft border-primary text-amethyst-ink" : "bg-card border-clay-border text-body hover:border-primary/40")}>
              <div className={cn("w-5 h-5 rounded-sm border-2 flex items-center justify-center", quickAddFields.includes(key) ? "bg-primary border-primary" : "border-clay-border")}>
                {quickAddFields.includes(key) && <Check className="w-3 h-3 text-white" />}
              </div>
              {label}
            </button>
          ))}
        </div>
      </section>

      {/* Tags — Navigation link to dedicated page */}
      <a href="/settings/tags" className="flex items-center gap-3 bg-card rounded-lg border border-clay-border p-5 hover:border-primary/40 transition-colors">
        <Tag className="w-5 h-5 text-primary" /><div><div className="text-sm font-semibold text-ink">Tags</div><div className="text-[0.8125rem] text-faint">Manage tags and choose which appear as inventory filters</div></div>
      </a>

      {/* Spaces – Bulk Add */}
      {spaces.every((s) => s.type === "home") && (
        <section className="bg-card rounded-lg border border-clay-border p-6">
          <h2 className="font-semibold text-ink mb-4 flex items-center gap-2"><MapPin className="w-5 h-5 text-primary" />Spaces</h2>
          <div className="flex justify-center">
            <button onClick={() => setShowBulk(true)} className="px-4 py-2 rounded-md bg-amethyst-soft text-amethyst-ink text-sm font-medium hover:bg-amethyst-soft/70 transition-colors">Add Many Spaces</button>
          </div>
        </section>
      )}

      <Modal open={showBulk} onClose={() => setShowBulk(false)} title="Add Many Spaces" wide>
        <BulkSpacesWizard spaces={spaces} onComplete={() => { setShowBulk(false); toast.show("Spaces created!"); fetchSpaces(); }} />
      </Modal>

      {/* Household & Invitations */}
      <section className="bg-card rounded-lg border border-clay-border p-6">
        <h2 className="font-semibold text-ink mb-4 flex items-center gap-2"><Users className="w-5 h-5 text-primary" />Household</h2>
        {usage?.sharingEnabled !== false ? (
        <div className="flex gap-2 mb-4">
          <input type="email" placeholder="Invite by email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && sendInvite()} className="flex-1 px-3 py-2.5 rounded-sm border border-clay-border text-sm text-body placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary" />
          <button onClick={sendInvite} aria-label="Send invitation" className="px-3 py-2.5 rounded-md bg-primary text-white text-sm hover:bg-amethyst-ink transition-colors"><Mail className="w-4 h-4" /></button>
        </div>
        ) : (
        <p className="text-sm text-body mb-4">Household sharing is available on Plus and Pro plans. <a href="/pricing" className="text-primary underline">Upgrade</a></p>
        )}

        {invitations.received.length > 0 && (
          <div className="mb-4">
            <h3 className="text-[0.8125rem] font-medium text-faint uppercase tracking-wide mb-2">Received Invitations</h3>
            {invitations.received.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between p-3 rounded-md bg-amethyst-soft mb-1">
                <span className="text-sm text-body">From {inv.invitedByName}</span>
                <div className="flex gap-2">
                  <button onClick={() => acceptInvite(inv.id)} className="px-3 py-1 rounded-md bg-primary text-white text-[0.8125rem] hover:bg-amethyst-ink transition-colors">Accept</button>
                  <button onClick={() => rejectInvite(inv.id)} className="px-3 py-1 rounded-md bg-clay-muted text-body text-[0.8125rem] hover:bg-clay-muted/70 transition-colors">Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {invitations.sent.length > 0 && (
          <div>
            <h3 className="text-[0.8125rem] font-medium text-faint uppercase tracking-wide mb-2">Sent Invitations</h3>
            {invitations.sent.map((inv) => (
              <div key={inv.id} className="flex items-center justify-between p-2 text-sm text-body">
                <span>{inv.email}</span>
                <span className={cn("text-[0.8125rem] px-2 py-0.5 rounded-pill", inv.status === "pending" ? "bg-warning-wash text-warning-text" : inv.status === "accepted" ? "bg-success-wash text-success-text" : "bg-clay-muted text-faint")}>{inv.status}</span>
              </div>
            ))}
          </div>
        )}
      </section>

      {/* Privacy & Terms */}
      <section className="bg-card rounded-lg border border-clay-border p-6">
        <h2 className="font-semibold text-ink mb-4 flex items-center gap-2"><Shield className="w-5 h-5 text-primary" />Privacy &amp; Terms</h2>
        <div className="space-y-2">
          <a href="https://www.homethyst.com/privacy" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-md border border-clay-border text-sm text-body hover:border-primary/40 transition-colors">
            <Shield className="w-4 h-4 text-primary" />Privacy Policy
          </a>
          <a href="https://www.homethyst.com/terms" target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 p-3 rounded-md border border-clay-border text-sm text-body hover:border-primary/40 transition-colors">
            <FileText className="w-4 h-4 text-primary" />Terms of Service
          </a>
        </div>
      </section>

      {/* AI Features — moved to near end */}
      <section className="bg-card rounded-lg border border-clay-border p-6">
        <h2 className="font-semibold text-ink mb-4 flex items-center gap-2"><Sparkles className="w-5 h-5 text-sage-deep" />AI Features</h2>
        <button onClick={async () => {
          const next = !aiEnabled;
          setAiEnabled(next);
          try {
            await apiFetch("/api/settings", { method: "PUT", body: JSON.stringify({ aiEnabled: next }) });
            refreshFlags();
            refreshDashboard();
            toast.show(next ? "AI features enabled" : "AI features disabled");
          } catch {
            setAiEnabled(!next);
            toast.show("Failed to update setting", "error");
          }
        }} aria-pressed={aiEnabled} className={cn("w-full flex items-center gap-3 p-3 rounded-md border text-left text-sm transition-colors", aiEnabled ? "bg-sage-wash border-sage-deep text-body" : "bg-card border-clay-border text-body hover:border-sage-deep/40")}>
          <div className={cn("w-5 h-5 rounded-sm border-2 flex items-center justify-center", aiEnabled ? "bg-sage-deep border-sage-deep" : "border-clay-border")}>
            {aiEnabled && <Check className="w-3 h-3 text-white" />}
          </div>
          Enable AI-powered features
        </button>
        <p className="text-[0.8125rem] text-faint mt-2">Photo identification, visual scan, AI parsing, and photo analysis.</p>
      </section>
    </div>
  );
}
